import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import type { Cell, ColumnDef, RowData } from "@tanstack/react-table";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

import clsx from "clsx";

import { Loader, Text } from "shared/uikit";
import { TextVariantsEnum } from "shared/uikit/types";

import { TableContainer } from "./style";

interface ValidationReviewModel {
	success: number[];
	warning: number[];
	error: number[];
}

export enum ColumnTypes {
	DEFAULT = "DEFAULT",
	CUSTOM = "CUSTOM"
}

export enum BulkTableRowStatuses {
	DEFAULT = "DEFAULT",
	SUCCESS = "SUCCESS",
	WARNING = "WARNING",
	ERROR = "ERROR"
}

export type BulkTableMethods = {
	getData: () => TableData[];
};

export interface TableData {
	[key: string]: any;
}

interface BulkTableProps {
	rowsCount?: number;
	defaultData?: TableData[];
	columns: Array<ColumnDef<TableData>>;
	onChangeReviewInfo?: (data: { success: number; warning: number; error: number }) => void;
	checkRowStatus: (rowData: TableData) => BulkTableRowStatuses;
}

declare module "@tanstack/react-table" {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface TableMeta<TData extends RowData> {
		updateData: (rowIndex: number, columnId: string, value: unknown) => void;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface ColumnMeta<TData, TValue> {
		type?: ColumnTypes;
		customField?: string;
		isRequired?: boolean;
	}
}

/**
 * BulkTable component for handling large datasets with virtualization
 */
const BulkTable = forwardRef<BulkTableMethods, BulkTableProps>(
	({ rowsCount = 50, defaultData, columns, onChangeReviewInfo, checkRowStatus }, ref) => {
		// Create individual objects for each row instead of sharing a reference
		const initialData = useMemo(() => {
			if (defaultData) return defaultData;
			return Array.from({ length: rowsCount }, () => ({}));
		}, [defaultData, rowsCount]);

		const [data, setData] = useState<TableData[]>(initialData);
		const [loading, setLoading] = useState(true);
		const [wrapperLoading, setWrapperLoading] = useState(false);
		const [, setReviewInfo] = useState<ValidationReviewModel>({
			success: [],
			warning: [],
			error: []
		});

		const parentRef = useRef<HTMLDivElement>(null);
		const tableData = useRef<TableData[]>(initialData);
		const activeCell = useRef<{
			rowIndex: number;
			columnIndex: number;
		}>({
			rowIndex: 0,
			columnIndex: 0
		});

		// Timeout reference for cleanup
		const timeoutRef = useRef<NodeJS.Timeout | null>(null);

		// Extract method for applying border styling to cells
		const applyRowStatusBorder = useCallback((rowIndex: number, rowValidation: BulkTableRowStatuses) => {
			if (!parentRef.current) return;

			const row = parentRef.current.querySelector(`tr[data-index="${rowIndex}"]`);
			if (!row) return;

			const cell = row.querySelector('td[data-index="0"]');
			if (!cell?.children[0]?.children[1]) return;

			const element = cell.children[0].children[1] as HTMLElement;
			element.classList.remove("border_default", "border_success", "border_error", "border_warning");
			element.classList.add(`border_${rowValidation}`);
		}, []);

		// Extract method for updating row status in reviewInfo
		const updateRowStatus = useCallback(
			(currentReviewInfo: ValidationReviewModel, rowIndex: number, rowValidation: BulkTableRowStatuses) => {
				const success = [...currentReviewInfo.success];
				const warning = [...currentReviewInfo.warning];
				const error = [...currentReviewInfo.error];

				// Remove row from all status arrays first
				const successIndex = success.indexOf(rowIndex);
				const warningIndex = warning.indexOf(rowIndex);
				const errorIndex = error.indexOf(rowIndex);

				if (successIndex !== -1) success.splice(successIndex, 1);
				if (warningIndex !== -1) warning.splice(warningIndex, 1);
				if (errorIndex !== -1) error.splice(errorIndex, 1);

				// Add to appropriate status array
				switch (rowValidation) {
					case BulkTableRowStatuses.WARNING:
						warning.push(rowIndex);
						break;
					case BulkTableRowStatuses.SUCCESS:
						success.push(rowIndex);
						break;
					case BulkTableRowStatuses.ERROR:
						error.push(rowIndex);
						break;
				}

				return { success, warning, error };
			},
			[]
		);

		// Optimize recalculateSummary with batching
		const recalculateSummary = useCallback(
			(rowIndexes: number[], highlightRows = false) => {
				// Batch updates to avoid excessive re-renders
				setReviewInfo(currentReviewInfo => {
					let updatedReviewInfo = { ...currentReviewInfo };

					// Process batches of rows for better performance
					const batchSize = 50;
					for (let i = 0; i < rowIndexes.length; i += batchSize) {
						const batch = rowIndexes.slice(i, i + batchSize);

						batch.forEach(rowIndex => {
							// Safety check for valid row
							if (rowIndex < 0 || rowIndex >= tableData.current.length || !tableData.current[rowIndex]) {
								return;
							}

							try {
								const rowValidation = checkRowStatus(tableData.current[rowIndex]);
								// Update review info state
								updatedReviewInfo = updateRowStatus(updatedReviewInfo, rowIndex, rowValidation);

								// Update UI if needed (but only for visible rows)
								if (highlightRows) {
									applyRowStatusBorder(rowIndex, rowValidation);
								}
							} catch (error) {
								console.error("Error processing row:", error);
							}
						});
					}

					// Notify parent component about changes
					onChangeReviewInfo?.({
						success: updatedReviewInfo.success.length,
						warning: updatedReviewInfo.warning.length,
						error: updatedReviewInfo.error.length
					});

					return updatedReviewInfo;
				});
			},
			[checkRowStatus, onChangeReviewInfo, applyRowStatusBorder, updateRowStatus]
		);

		// Cleanup timeout on unmount
		useEffect(() => {
			return () => {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
			};
		}, []);

		// Process initial data load with delay and batching
		useEffect(() => {
			setLoading(false);

			// Use smaller batches for initial calculation to improve perceived performance
			const processInitialData = () => {
				const allRows = tableData.current.map((_, index) => index);
				const batchSize = 100;

				const processBatch = (startIdx: number) => {
					if (startIdx >= allRows.length) return;

					const endIdx = Math.min(startIdx + batchSize, allRows.length);
					const batch = allRows.slice(startIdx, endIdx);

					recalculateSummary(batch, true);

					// Schedule the next batch
					timeoutRef.current = setTimeout(() => {
						processBatch(endIdx);
					}, 10);
				};

				processBatch(0);
			};

			timeoutRef.current = setTimeout(processInitialData, 150);

			return () => {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
			};
		}, [recalculateSummary]);

		// Optimize cell update for better performance
		const updateCellData = useCallback(
			(rowIndex: number, columnId: string, value: any) => {
				// Ensure valid row index
				if (rowIndex < 0 || rowIndex >= tableData.current.length) {
					return;
				}

				// Update only the specific cell without rebuilding the entire array
				const updatedRow = {
					...tableData.current[rowIndex],
					[columnId]: value
				};

				tableData.current[rowIndex] = updatedRow;

				setData([...tableData.current]);

				try {
					const rowValidation = checkRowStatus(updatedRow);
					applyRowStatusBorder(rowIndex, rowValidation);
					recalculateSummary([rowIndex]);
				} catch (error) {
					console.error("Error updating cell:", error);
				}
			},
			[checkRowStatus, applyRowStatusBorder, recalculateSummary]
		);

		// Use useReactTable properly (not inside useMemo)
		const table = useReactTable({
			data,
			columns,
			defaultColumn: {
				size: 200
			},
			getCoreRowModel: getCoreRowModel(),
			meta: {
				updateData: updateCellData
			},
			debugTable: false
		});

		const { rows } = table.getRowModel();

		// Optimize virtualizer for better performance
		const virtualizer = useVirtualizer({
			count: Math.min(rows.length, rowsCount),
			getScrollElement: () => parentRef.current,
			estimateSize: () => 54, // row height
			overscan: 20, // Reduced for better performance with large datasets
			measureElement:
				typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
					? element => element?.getBoundingClientRect().height
					: undefined
		});

		// Optimize paste handler for better performance with batching
		const handlePaste = useCallback(
			(e: React.ClipboardEvent) => {
				e.preventDefault();
				const clipboardData = e.clipboardData.getData("Text");

				if (!clipboardData) return;

				const pastedRows = clipboardData
					.trim()
					.split("\n")
					.map(row => row.split("\t"));

				const replaceExtraSymbols = (val?: null | string) => {
					if (typeof val === "string" && val.endsWith("/r")) {
						return val.replace("/r", "");
					}
					return val || "";
				};

				// Create a shallow copy of the current data
				const refreshedData = [...tableData.current];
				const updatedRows: number[] = [];

				setWrapperLoading(true);
				// Process pasted data in batches for better performance
				const processRow = (rowIdx: number) => {
					if (rowIdx >= pastedRows.length) {
						// All rows processed, update state and recalculate
						tableData.current = refreshedData;
						setData(refreshedData);

						// Process status updates in smaller batches
						const batchSize = 50;
						for (let i = 0; i < updatedRows.length; i += batchSize) {
							const batch = updatedRows.slice(i, i + batchSize);
							setTimeout(
								() => {
									recalculateSummary(batch, true);
								},
								(i / batchSize) * 10
							); // Stagger the updates
							setWrapperLoading(false);
						}
						return;
					}

					const row = pastedRows[rowIdx];
					const rowIndex = activeCell.current.rowIndex + rowIdx;

					// Skip if row index is out of bounds
					if (rowIndex >= refreshedData.length) {
						processRow(rowIdx + 1);
						return;
					}

					const objectInfo: TableData = {};

					row?.forEach((val, cIndex) => {
						const colIndex = activeCell.current.columnIndex + cIndex;
						if (columns.length > colIndex) {
							const field = columns[colIndex]?.meta?.customField;
							if (field) {
								objectInfo[field] = replaceExtraSymbols(val);
							}
						}
					});

					refreshedData[rowIndex] = {
						...refreshedData[rowIndex],
						...objectInfo
					};
					updatedRows.push(rowIndex);

					// Process next row with small delay to avoid blocking UI
					setTimeout(() => processRow(rowIdx + 1), 0);
				};

				// Start processing rows
				processRow(0);
			},
			[columns, recalculateSummary]
		);

		// Handle cell selection
		const handleCellSelect = useCallback((cell: Cell<any, unknown>) => {
			activeCell.current = {
				rowIndex: cell.row.index,
				columnIndex: cell.column.getIndex()
			};
		}, []);

		// Improved keyboard navigation
		const handleKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLElement>) => {
				const newCellPos = {
					rowIndex: activeCell.current.rowIndex,
					columnIndex: activeCell.current.columnIndex
				};

				switch (event.key) {
					case "ArrowDown":
						if (activeCell.current.rowIndex < rows.length - 1) {
							newCellPos.rowIndex = activeCell.current.rowIndex + 1;
						}
						break;
					case "ArrowUp":
						if (activeCell.current.rowIndex > 0) {
							newCellPos.rowIndex = activeCell.current.rowIndex - 1;
						}
						break;
					case "ArrowRight":
					case "Tab":
						if (activeCell.current.columnIndex < columns.length - 2) {
							newCellPos.columnIndex = activeCell.current.columnIndex + 1;
						}
						break;
					case "ArrowLeft":
						if (activeCell.current.columnIndex > 1) {
							newCellPos.columnIndex = activeCell.current.columnIndex - 1;
						}
						break;
					default:
						return; // Exit early for other keys
				}

				const isCellChanged =
					activeCell.current.rowIndex !== newCellPos.rowIndex ||
					activeCell.current.columnIndex !== newCellPos.columnIndex;

				if (isCellChanged) {
					event.preventDefault(); // Prevent default arrow key behavior
					activeCell.current = newCellPos;

					// Use requestAnimationFrame to focus element after rendering
					requestAnimationFrame(() => {
						if (!parentRef.current) return;

						const row = parentRef.current.querySelector(`tr[data-index="${activeCell.current.rowIndex}"]`);
						if (!row) return;

						const cell = row.querySelector(`td[data-index="${activeCell.current.columnIndex}"]`);
						if (!cell) return;

						const input = cell.querySelector("input");
						if (input instanceof HTMLElement) {
							input.focus();
						}
					});
				}
			},
			[rows.length, columns.length]
		);

		// Expose methods to parent component
		useImperativeHandle(
			ref,
			() => ({
				getData: () => {
					setData([...tableData.current]); // Update state with current data
					return tableData.current;
				}
			}),
			[]
		);

		// Memoize header rendering for better performance
		const tableHeader = useMemo(() => {
			return table.getHeaderGroups().map(headerGroup => (
				<tr key={headerGroup.id} style={{ display: "flex", width: "100%" }}>
					{headerGroup.headers.map(header => (
						<th key={header.id} colSpan={header.colSpan} style={{ display: "flex", width: header.getSize() }}>
							{header.isPlaceholder ? null : (
								<div className="flex items-center justify-center p-2.5">
									<Text variants={TextVariantsEnum.BodyMedium}>
										{flexRender(header.column.columnDef.header, header.getContext())}
									</Text>
								</div>
							)}
						</th>
					))}
				</tr>
			));
		}, [table]);

		if (loading) {
			return <Loader />;
		}

		return (
			<TableContainer>
				{wrapperLoading && (
					<div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white/50 z-50">
						<Loader />
					</div>
				)}
				<div
					ref={parentRef}
					onPaste={handlePaste}
					onKeyDown={handleKeyDown}
					style={{
						overflow: "auto",
						height: "100%",
						overflowAnchor: "none",
						position: "relative"
					}}
					tabIndex={0} // Make div focusable for keyboard events
					role="grid" // Improve accessibility
					aria-rowcount={rows.length}
					aria-colcount={columns.length}
				>
					<table>
						<thead
							style={{
								position: "sticky",
								top: 0,
								zIndex: 1
							}}
						>
							{tableHeader}
						</thead>
						<tbody
							style={{
								height: `${virtualizer.getTotalSize()}px`,
								position: "relative"
							}}
						>
							{virtualizer.getVirtualItems().map(virtualRow => {
								const row = rows[virtualRow.index];
								if (!row) return null;

								const visibleCells = row.getVisibleCells();

								return (
									<tr
										data-index={virtualRow.index}
										ref={node => virtualizer.measureElement(node)}
										key={row.id}
										style={{
											display: "flex",
											width: "100%",
											position: "absolute",
											height: `${virtualRow.size}px`,
											transform: `translateY(${virtualRow.start}px)`
										}}
										role="row"
										aria-rowindex={virtualRow.index + 1}
									>
										{visibleCells.map(cell => (
											<td
												data-index={cell.column.getIndex()}
												className={clsx(cell.column.columnDef?.meta?.type === ColumnTypes.DEFAULT && "body-td")}
												key={cell.id}
												style={{
													display: "flex",
													alignItems: "center",
													width: cell.column.getSize(),
													minWidth: cell.column.getSize(),
													maxWidth: cell.column.getSize()
												}}
												onClick={() => handleCellSelect(cell)}
												role="gridcell"
												aria-colindex={cell.column.getIndex() + 1}
											>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										))}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</TableContainer>
		);
	}
);

// Add displayName for better debugging
BulkTable.displayName = "BulkTable";

export default BulkTable;
